import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

//components
import {
	Button,
	Col,
	Container,
	FormControl,
	InputGroup,
	Row,
	Spinner,
	Tab,
	Tabs,
} from "react-bootstrap";
import { CSVLink } from "react-csv";
import { FaSearch } from "react-icons/fa";
import { MdPersonSearch } from "react-icons/md";
import { toast } from "react-toastify";
import { Modal } from "../../../../components/modal";
import { PaginationList } from "../../../../components/pagination";
import { UsersTable } from "../../components/table-users";
import { ModalSignature } from "../../subscriptions/modal-signature";
import { ContentBody, ContentHeader } from "./styled";

import { useHistory } from "react-router-dom";
import { IUserManager } from "../../../../models/IUserManager";
import { cancelSubscription } from "../../../../services/manager/signature.service";
import {
	changeUserState,
	getLeads,
	getUsers,
	getUsersGuests,
	getUsersPartners,
	getUsersSubscribers,
	removeUser,
} from "../../../../services/manager/user.service";

const DEFAULT_VALUE: IUserManager = {
	id: 0,
	name: "",
	company: "",
	job: "",
	phone: "",
	email: "",
	picture: "",
	signature: {
		id: 0,
		start: "",
		end: "",
		is_active: false,
		status: 1,
	},
	username: "",
	created_at: "",
	deleted_at: "",
	updated_at: "",
	user_information: {},
	is_superuser: false,
	is_staff: false,
	is_active: false,
	is_whatsapp: false,
	is_signature: false,
};

export const UsersList = (): JSX.Element => {
	const { t: translation } = useTranslation();
	const history = useHistory();

	//Pagination
	const [totalItens, setTotalItens] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);

	//Ordering
	const [sortOrder, setSortOrder] = useState(1);
	const [sortField, setSortField] = useState("name");

	const [loading, setLoading] = useState<boolean>(true);
	const [loadingModal, setLoadingModal] = useState<boolean>(false);
	const [isRenewSignature, setIsRenewSignature] = useState<boolean>(false);
	const [showEmptyMessage, setShowEmptyMessage] = useState<boolean>(false);
	const [user, setUser] = useState<IUserManager>(DEFAULT_VALUE);
	const [showModalSignature, setShowModalSignature] =
		useState<boolean>(false);
	const [showPartnerNotice, setShowPartnerNotice] = useState<boolean>(false);
	const [showRemoveUserNotice, setShowRemoveUserNotice] =
		useState<boolean>(false);
	const [showCancelSignatureNotice, setShowCancelSignatureNotice] =
		useState<boolean>(false);
	const [cleanQueryField, setCleanQueryField] = useState<boolean>(false);
	const [showExportCsv, setShowExportCsv] = useState<boolean>(false);

	const [key, setKey] = useState<string>("all");
	const [query, setQuery] = useState("");
	const [usersList, setUsersList] = useState([]);
	const [usersPartnersList, setUsersPartnersList] = useState([]);
	const [usersSubscribersList, setUsersSubscribersList] = useState([]);
	const [usersGuestsList, setUsersGuestsList] = useState([]);
	const [leadsList, setLeadsList] = useState([]);
	const [leadsListCsv, setLeadsListCsv] = useState([]);

	const queryParams = new URLSearchParams(window.location.search);
	const listKey = queryParams.get("key");

	const treeTableConfig = [
		{
			field: "name",
			header: "Nome",
			nameTemplate: true,
			sortable: true,
			style: { textAlign: "left", width: "35%" },
		},
		{
			field: "status",
			className: "d-none-xs-table d-none-md-table font-14",
			header: "Status",
			sortable: true,
			style: { textAlign: "center", width: "9%" },
		},
		{
			field: "user_information",
			className: "d-none-xxs-table",
			header: "Informações de uso",
			infoUseTemplate: true,
			sortable: false,
			style: { textAlign: "center" },
		},
		{
			field: "last_login",
			className: "d-none-xs-table d-none-md-table d-none-lg-table",
			header: "Último login",
			lastLoginTemplate: true,
			sortable: true,
			style: { textAlign: "center" },
		},
		{
			field: "signature",
			className: "d-none-xxs-table",
			header: "Informações de assinatura",
			infoSignatureTemplate: true,
			sortable: true,
			style: { textAlign: "center" },
		},
		{
			field: "actions",
			header: "",
			actionsTemplate: true,
			sortable: false,
			style: { textAlign: "center", width: "7%" },
		},
	];

	const leadTableConfig = [
		{
			field: "name",
			header: "Nome",
			sortable: true,
			style: { textAlign: "left" },
		},
		{
			field: "email",
			header: "E-mail",
			sortable: true,
			style: { textAlign: "left" },
		},
		{
			field: "phone",
			header: "Telefone",
			sortable: true,
			style: { textAlign: "left" },
		},
		{
			field: "created_at",
			header: "Data de captura",
			sortable: true,
			dateCaptureTemplate: true,
			style: { display: "flex", justifyContent: "center" },
		},
	];

	useEffect(() => {
		if (listKey) {
			handleChangeTab(listKey);
		} else {
			getUsersList();
		}
	}, []);

	useEffect(() => {
		if (!loadingModal) {
			switch (key) {
				case "partners":
					getPartnersList();
					break;

				case "subscribers":
					getSubscribersList(`page=${currentPage}&q=${query}`);
					break;

				case "guests":
					getGuestsList();
					break;

				default:
					getUsersList();
			}
		}
	}, [loadingModal]);

	useEffect(() => {
		if (!cleanQueryField) {
			const timeOutId = setTimeout(() => {
				switch (key) {
					case "partners":
						getPartnersList(`q=${query}`);
						break;

					case "subscribers":
						getSubscribersList(`q=${query}`);
						break;

					case "guests":
						getGuestsList(`q=${query}`);
						break;

					case "leads":
						setShowExportCsv(true);
						getLeadsList(`q=${query}`);
						break;

					default:
						if (!listKey) {
							getUsersList(`q=${query}`);
						}
				}
			}, 500);
			return () => clearTimeout(timeOutId);
		} else {
			setCleanQueryField(false);
		}
	}, [query]);

	const getUsersList = (_query?: string) => {
		setLoading(true);
		getUsers(_query).then((data) => {
			setUsersList(data.results);
			if (data.results.length === 0) {
				setShowEmptyMessage(true);
			} else {
				setShowEmptyMessage(false);
			}
			setTotalItens(data.count);
			setLoading(false);
		});
	};

	const getPartnersList = (_query?: string) => {
		setLoading(true);
		getUsersPartners(_query).then((data) => {
			setUsersPartnersList(data.results);
			if (data.results.length === 0) {
				setShowEmptyMessage(true);
			} else {
				setShowEmptyMessage(false);
			}
			setTotalItens(data.count);
			setLoading(false);
		});
	};

	const getSubscribersList = (_query?: string) => {
		setLoading(true);
		getUsersSubscribers(_query).then((data) => {
			setUsersSubscribersList(data.results);
			if (data.results.length === 0) {
				setShowEmptyMessage(true);
			} else {
				setShowEmptyMessage(false);
			}
			setTotalItens(data.count);
			setLoading(false);
		});
	};

	const getGuestsList = (_query?: string) => {
		setLoading(true);
		getUsersGuests(_query).then((data) => {
			setUsersGuestsList(data.results);
			if (data.results.length === 0) {
				setShowEmptyMessage(true);
			} else {
				setShowEmptyMessage(false);
			}
			setTotalItens(data.count);
			setLoading(false);
		});
	};

	const getLeadsList = (_query?: string) => {
		setLoading(true);
		getLeads(_query).then((data) => {
			setLeadsList(data.results);
			if (data.results.length === 0) {
				setShowEmptyMessage(true);
			} else {
				setShowEmptyMessage(false);
			}
			getLeadsListCsv();
			setTotalItens(data.count);
			setLoading(false);
		});
	};

	const getLeadsListCsv = () => {
		let order = sortOrder > 0 ? sortField : "-" + sortField;
		getLeads(`get_all=True&ordering=${order}`).then((data) => {
			setLeadsListCsv(data);
		});
	};

	const handleTableSort = (event: any) => {
		let order =
			event.sortOrder > 0 ? event.sortField : "-" + event.sortField;

		setSortOrder(event.sortOrder);
		setSortField(event.sortField);

		switch (key) {
			case "partners":
				getPartnersList(
					`page=${currentPage}&q=${query}&ordering=${order}`,
				);
				break;

			case "subscribers":
				getSubscribersList(
					`page=${currentPage}&q=${query}&ordering=${order}`,
				);
				break;

			case "guests":
				getGuestsList(
					`page=${currentPage}&q=${query}&ordering=${order}`,
				);
				break;

			case "leads":
				getLeadsList(
					`page=${currentPage}&q=${query}&ordering=${order}`,
				);
				break;

			default:
				getUsersList(
					`page=${currentPage}&q=${query}&ordering=${order}`,
				);
		}
	};

	const handlePageChange = (event?: any) => {
		setShowExportCsv(false);
		let order = sortOrder > 0 ? sortField : "-" + sortField;

		if (currentPage !== event) {
			switch (key) {
				case "partners":
					getPartnersList(
						`page=${event}&q=${query}&ordering=${order}`,
					);
					break;

				case "subscribers":
					getSubscribersList(
						`page=${event}&q=${query}&ordering=${order}`,
					);
					break;

				case "guests":
					getGuestsList(`page=${event}&q=${query}&ordering=${order}`);
					break;

				case "leads":
					setShowExportCsv(true);
					getLeadsList(`page=${event}&q=${query}&ordering=${order}`);
					break;

				default:
					getUsersList(`page=${event}&q=${query}&ordering=${order}`);
			}
			setCurrentPage(event);
		}
	};

	const handleChangeTab = (k: string) => {
		let order = sortOrder > 0 ? sortField : "-" + sortField;
		setCleanQueryField(true);
		setShowExportCsv(false);
		setQuery("");
		setKey(k);

		switch (k) {
			case "partners":
				getPartnersList();
				break;

			case "subscribers":
				getSubscribersList();
				break;

			case "guests":
				getGuestsList();
				break;

			case "leads":
				setShowExportCsv(true);
				getLeadsList(`ordering=${order}`);
				break;

			default:
				getUsersList();
		}
	};

	const handleSignature = (_user: any, renew: boolean) => {
		setIsRenewSignature(renew);
		setUser(_user);
		setShowModalSignature(true);
	};

	const handlePartnerActive = (_user: any) => {
		setUser(_user);
		setShowPartnerNotice(true);
	};

	const onSubmitPartnerActive = () => {
		setLoadingModal(true);
		changeUserState(user.id, !user.is_active)
			.then(() => {
				setShowPartnerNotice(false);
				setLoadingModal(false);
				getPartnersList();
			})
			.catch((error: any) => {
				console.log(error);
				setShowPartnerNotice(false);
				setLoadingModal(false);
			});
	};

	const handleCancelSignature = (_user: any) => {
		setUser(_user);
		setShowCancelSignatureNotice(true);
	};

	const onSubmitCancelSignature = () => {
		setLoadingModal(true);
		cancelSubscription(user.signature ? user.signature.id : "")
			.then(() => {
				toast.success(`Assinatura cancelada com sucesso!`);
				setShowCancelSignatureNotice(false);
				setLoadingModal(false);
				getSubscribersList(`page=${currentPage}&q=${query}`);
			})
			.catch((error: any) => {
				console.log(error);
				setShowPartnerNotice(false);
				setLoadingModal(false);
			});
	};

	const handleUserDetail = (node?: any, materialTab?: boolean) => {
		if (node && node.id && !materialTab) {
			return `/manager/user/${node.id}?key=${key}`;
		} else {
			return `/manager/user/${node.id}?materialTab=true&&key=${key}`;
		}
	};

	const handleRemoveUser = (_user: any) => {
		setUser(_user);
		setShowRemoveUserNotice(true);
	};

	const onSubmitRemoveUser = () => {
		setLoadingModal(true);
		removeUser(user.id)
			.then(() => {
				toast.success(`Usuário removido com sucesso!`);
				setShowRemoveUserNotice(false);
				setLoadingModal(false);
				getUsersList();
			})
			.catch((error: any) => {
				toast.error(
					`Ocorreu um erro ao remover este usuário. Tente novamente.`,
				);
				setShowRemoveUserNotice(false);
				setLoadingModal(false);
			});
	};

	const headers = [
		{ label: "Nome", key: "name" },
		{ label: "E-mail", key: "email" },
		{ label: "Telefone", key: "phone" },
		{ label: "Data de captura", key: "created_at" },
	];

	const csvReport = {
		data: leadsListCsv,
		headers: headers,
		filename: "leads_report.csv",
	};

	return (
		<Container fluid>
			<ContentHeader>
				<Row>
					<div className="title">
						{translation("manager.user.userManager")}
						<span className="subtitle">
							{totalItens} resultados
						</span>
					</div>
				</Row>
			</ContentHeader>
			<ContentBody>
				<Row className="mt-4">
					<Col
						xs={12}
						md={5}
					>
						<InputGroup className="mb-2 mt-sm-0 ds-input-group">
							<InputGroup.Text id="basic-addon1">
								<FaSearch></FaSearch>
							</InputGroup.Text>
							<FormControl
								className="input-text"
								placeholder={translation("filters.usersSearch")}
								aria-label={translation("filters.usersSearch")}
								aria-describedby="basic-addon1"
								value={query}
								onChange={(event: any) =>
									setQuery(event.target.value)
								}
							/>
						</InputGroup>
					</Col>
					{showExportCsv && (
						<Col>
							<Button>
								<CSVLink
									{...csvReport}
									style={{ color: "white" }}
								>
									Exportar CSV
								</CSVLink>
							</Button>
						</Col>
					)}
				</Row>

				<Row className="mt-3">
					<div id="tabs-materials">
						<Tabs
							className="tabs mb-3"
							activeKey={key}
							onSelect={(k: any) => handleChangeTab(k)}
						>
							<Tab
								eventKey="all"
								title={translation("manager.user.all")}
							>
								{loading ? (
									<div className="loading-container mt-5">
										<Spinner
											animation="border"
											variant="primary"
										/>
									</div>
								) : (
									<>
										{showEmptyMessage ? (
											<div className="text-center text-grey mt-5">
												<MdPersonSearch
													className="mt-5"
													size={100}
												/>
												<h5>
													{translation(
														"manager.user.emptyUser",
													)}
												</h5>
											</div>
										) : (
											<UsersTable
												treeFields={treeTableConfig}
												nodes={usersList}
												handleSignature={
													handleSignature
												}
												handlePartnerActive={
													handlePartnerActive
												}
												handleCancelSignature={
													handleCancelSignature
												}
												handleTableSort={
													handleTableSort
												}
												sortField={sortField}
												sortOrder={sortOrder}
												handleUserDetail={
													handleUserDetail
												}
												handleRemoveUser={
													handleRemoveUser
												}
											/>
										)}
									</>
								)}
							</Tab>
							<Tab
								eventKey="partners"
								title={translation("manager.user.partners")}
							>
								{loading ? (
									<div className="loading-container mt-5">
										<Spinner
											animation="border"
											variant="primary"
										/>
									</div>
								) : (
									<>
										{showEmptyMessage ? (
											<div className="text-center text-grey mt-5">
												<MdPersonSearch
													className="mt-5"
													size={100}
												/>
												<h5>
													{translation(
														"manager.user.emptyPartner",
													)}
												</h5>
											</div>
										) : (
											<UsersTable
												treeFields={treeTableConfig}
												nodes={usersPartnersList}
												handleSignature={
													handleSignature
												}
												handlePartnerActive={
													handlePartnerActive
												}
												handleCancelSignature={
													handleCancelSignature
												}
												handleTableSort={
													handleTableSort
												}
												sortField={sortField}
												sortOrder={sortOrder}
												handleUserDetail={
													handleUserDetail
												}
												handleRemoveUser={
													handleRemoveUser
												}
											/>
										)}
									</>
								)}
							</Tab>
							<Tab
								eventKey="subscribers"
								title={translation("manager.user.subscribers")}
							>
								{loading ? (
									<div className="loading-container mt-5">
										<Spinner
											animation="border"
											variant="primary"
										/>
									</div>
								) : (
									<>
										{showEmptyMessage ? (
											<div className="text-center text-grey mt-5">
												<MdPersonSearch
													className="mt-5"
													size={100}
												/>
												<h5>
													{translation(
														"manager.user.emptySubscriber",
													)}
												</h5>
											</div>
										) : (
											<UsersTable
												treeFields={treeTableConfig}
												nodes={usersSubscribersList}
												handleSignature={
													handleSignature
												}
												handlePartnerActive={
													handlePartnerActive
												}
												handleCancelSignature={
													handleCancelSignature
												}
												handleTableSort={
													handleTableSort
												}
												sortField={sortField}
												sortOrder={sortOrder}
												handleUserDetail={
													handleUserDetail
												}
												handleRemoveUser={
													handleRemoveUser
												}
											/>
										)}
									</>
								)}
							</Tab>
							<Tab
								eventKey="guests"
								title={translation("manager.user.guests")}
							>
								{loading ? (
									<div className="loading-container mt-5">
										<Spinner
											animation="border"
											variant="primary"
										/>
									</div>
								) : (
									<>
										{showEmptyMessage ? (
											<div className="text-center text-grey mt-5">
												<MdPersonSearch
													className="mt-5"
													size={100}
												/>
												<h5>
													{translation(
														"manager.user.emptyGuest",
													)}
												</h5>
											</div>
										) : (
											<UsersTable
												treeFields={treeTableConfig}
												nodes={usersGuestsList}
												handleSignature={
													handleSignature
												}
												handlePartnerActive={
													handlePartnerActive
												}
												handleCancelSignature={
													handleCancelSignature
												}
												handleTableSort={
													handleTableSort
												}
												sortField={sortField}
												sortOrder={sortOrder}
												handleUserDetail={
													handleUserDetail
												}
												handleRemoveUser={
													handleRemoveUser
												}
											/>
										)}
									</>
								)}
							</Tab>
							<Tab
								eventKey="leads"
								title={translation("lead.leads")}
							>
								{loading ? (
									<div className="loading-container mt-5">
										<Spinner
											animation="border"
											variant="primary"
										/>
									</div>
								) : (
									<>
										{showEmptyMessage ? (
											<div className="text-center text-grey mt-5">
												<MdPersonSearch
													className="mt-5"
													size={100}
												/>
												<h5>
													{translation(
														"lead.emptyLeads",
													)}
												</h5>
											</div>
										) : (
											<UsersTable
												treeFields={leadTableConfig}
												nodes={leadsList}
												handleTableSort={
													handleTableSort
												}
												sortField={sortField}
												sortOrder={sortOrder}
											/>
										)}
									</>
								)}
							</Tab>
						</Tabs>
					</div>
				</Row>
				<Row className="mt-5">
					<PaginationList
						currentPage={currentPage}
						totalItems={totalItens}
						onPageChange={handlePageChange}
					></PaginationList>
				</Row>
			</ContentBody>

			<ModalSignature
				showModal={showModalSignature}
				setShowModal={setShowModalSignature}
				isNewSignture={isRenewSignature}
				user={user}
				loading={loadingModal}
				setLoading={setLoadingModal}
			/>

			<Modal
				headerTitle="Aviso"
				submitButtonText={
					user.is_active
						? translation("manager.user.inactivate")
						: translation("manager.user.reactivate")
				}
				show={showPartnerNotice}
				onHide={() => setShowPartnerNotice(false)}
				onSubmit={onSubmitPartnerActive}
				variant="confirm-modal-default"
				loading={loadingModal}
			>
				{user.is_active ? (
					<p>
						Tem certeza que deseja inativar o parceiro{" "}
						<b className="span-ellipsis">{user.name}</b>?
					</p>
				) : (
					<p>
						Tem certeza que deseja reativar o parceiro{" "}
						<b className="span-ellipsis">{user.name}</b>?
					</p>
				)}
			</Modal>

			<Modal
				headerTitle="Aviso"
				submitButtonText={translation("manager.links.remove")}
				show={showRemoveUserNotice}
				onHide={() => setShowRemoveUserNotice(false)}
				onSubmit={onSubmitRemoveUser}
				variant="confirm-modal"
				loading={loadingModal}
			>
				<>
					<p>
						Tem certeza que deseja excluir o usuário{" "}
						<b>{user.name}</b>?
					</p>
					{user.user_information.materials_used ||
						user.user_information.registered_materials ||
						(user.user_information.visualized_materials && (
							<p>
								Este usuário tem{" "}
								{user.user_information.materials_used ||
									user.user_information
										.registered_materials ||
									user.user_information
										.visualized_materials}{" "}
								materiais vinculados. <br />
								Todos os materiais serão excluídos com o
								usuário.
							</p>
						))}
				</>
			</Modal>

			<Modal
				headerTitle="Aviso"
				submitButtonText={translation("manager.user.confirm")}
				show={showCancelSignatureNotice}
				onHide={() => setShowCancelSignatureNotice(false)}
				onSubmit={onSubmitCancelSignature}
				variant="confirm-modal-default"
				loading={loadingModal}
			>
				<p>
					Tem certeza que deseja cancelar a assinatura do usuário{" "}
					<b>{user.name}</b>?
				</p>
			</Modal>
		</Container>
	);
};
